// CSNotificationBanner.tsx
import {
    XMarkIcon,
    ExclamationTriangleIcon,
    ExclamationCircleIcon,
    CheckCircleIcon,
    InformationCircleIcon,
} from "@heroicons/react/24/solid";
import { useCustomerLoyalty } from "../../pages/customer-space/CustomerLoyaltyContext";

export default function CSNotificationBanner() {
    const { notification, clearNotification } = useCustomerLoyalty();
    if (!notification) return null;

    const { type, message, title: customTitle } = notification;

    const theme =
        {
            // Erreur (rouge, triangle dans screenshot)
            erreur: {
                gradient: "from-rose-50 to-rose-100",
                defaultTitle: "Erreur",
                icon: <ExclamationTriangleIcon className="h-6 w-6 text-rose-600" />,
                titleColor: "text-slate-900",
                textColor: "text-slate-700",
            },
            // Alerte / Warning (jaune)
            alert: {
                gradient: "from-amber-50 to-orange-100",
                defaultTitle: "Attention",
                icon: <ExclamationCircleIcon className="h-6 w-6 text-amber-600" />,
                titleColor: "text-slate-900",
                textColor: "text-slate-700",
            },
            // Succès (vert)
            succes: {
                gradient: "from-emerald-50 to-green-100",
                defaultTitle: "Succès",
                icon: <CheckCircleIcon className="h-6 w-6 text-emerald-600" />,
                titleColor: "text-slate-900",
                textColor: "text-slate-700",
            },
        }[type] ||
        // Info (bleu) — fallback
        {
            gradient: "from-sky-50 to-indigo-100",
            defaultTitle: "Information",
            icon: <InformationCircleIcon className="h-6 w-6 text-sky-600" />,
            titleColor: "text-slate-900",
            textColor: "text-slate-700",
        };

    // Utiliser le titre personnalisé s'il est fourni, sinon le titre par défaut
    const displayTitle = customTitle || theme.defaultTitle;

    return (
        <div className="pointer-events-none fixed top-0 left-0 right-0 z-50 flex justify-center p-4">
            <div
                className={[
                    "pointer-events-auto mx-2 w-full max-w-3xl",
                    "rounded-3xl bg-gradient-to-r", // dégradé doux
                    theme.gradient,
                    // ombre et légère bordure pour l'effet carte flottante
                    "shadow-[0_12px_40px_rgba(2,6,23,0.12)] ring-1 ring-black/5",
                    "px-6 py-5",
                ].join(" ")}
            >
                <div className="flex items-start gap-4">
                    {/* Médaillon icône style "glass" */}
                    <div
                        className={[
                            "mt-0.5 flex h-12 w-12 items-center justify-center rounded-full",
                            "bg-white/80 backdrop-blur",
                            "ring-1 ring-black/10",
                            "shadow-[inset_0_1px_0_rgba(255,255,255,0.8),0_4px_12px_rgba(2,6,23,0.08)]",
                        ].join(" ")}
                        aria-hidden
                    >
                        {theme.icon}
                    </div>

                    {/* Texte */}
                    <div className="flex-1">
                        <div className={["text-lg font-semibold leading-6", theme.titleColor].join(" ")}>
                            {displayTitle}
                        </div>
                        <div className={["mt-1 text-sm leading-6", theme.textColor].join(" ")}>
                            {message}
                        </div>
                    </div>

                    {/* Bouton close rond discret */}
                    <button
                        type="button"
                        onClick={clearNotification}
                        aria-label="Fermer la notification"
                        className={[
                            "ml-2 inline-flex h-8 w-8 items-center justify-center rounded-full",
                            "bg-white/70 hover:bg-white transition",
                            "ring-1 ring-black/10 shadow-[0_1px_2px_rgba(2,6,23,0.06)]",
                        ].join(" ")}
                    >
                        <XMarkIcon className="h-5 w-5 text-slate-600" />
                    </button>
                </div>
            </div>
        </div>
    );
}
